import { useState } from 'react';
import { ArrowSquareOutIcon, XIcon } from '@phosphor-icons/react';
import LureoButton from '@/common/LureoButton';
import { Input } from '@/components/cms/ui/input';
import { DialogBody, DialogFooter, DialogHeader } from '@/components/cms/ui/dialog';
import { IconButton } from '@/components/ui/icon-button';
import { TextLink } from '@/components/ui/text-button';
import { FieldError } from './FieldMarks';
import { Field } from '@/modules/primitives3D/editorShell';
import { CardTitle, FieldLabel, FieldHint } from '@/components/ui/text';
import { useOrganization } from '@/hooks/useOrganization';

const IMMERSAL_PORTAL_URL = 'https://developers.immersal.com/';

interface Props {
  onClose: () => void;
  onSaved?: () => void;
}

export function ImmersalTokenOverlay({ onClose, onSaved }: Props) {
  const { saveImmersalToken } = useOrganization();
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    const trimmed = token.trim();
    if (!trimmed) {
      setError('Enter your developer token.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await saveImmersalToken(trimmed);
      if (saved) {
        onSaved?.();
        onClose();
      } else {
        setError("Token wasn't saved. Please try again.");
      }
    } catch {
      setError('Could not save your token. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    // A panel over the dialog it opens in, with that dialog's parts; its close returns to the dialog
    <div className="absolute inset-0 z-30 flex flex-col bg-lureo-surface">
      <DialogHeader>
        {/* Not a DialogTitle: the dialog underneath already has the one title Radix labels it by */}
        <CardTitle>Add your Immersal developer token</CardTitle>
      </DialogHeader>
      <IconButton label="Close" place="dialog" onClick={onClose} className="absolute right-4 top-3">
        <XIcon className="h-4 w-4" />
      </IconButton>

      <DialogBody>
        <p className="font-lureo-font-prose text-lureo-14 text-lureo-ink-subtle leading-normal">
          Add your organisation's Immersal developer token so web stories with an Immersal
          scan run on Android. Saved once, reused everywhere.
        </p>

        <TextLink tone="action" href={IMMERSAL_PORTAL_URL} target="_blank" rel="noopener noreferrer" className="w-fit">
          Get your token from the Immersal Developer Portal
          <ArrowSquareOutIcon />
        </TextLink>

        <Field>
          <FieldLabel mb>Developer token</FieldLabel>
          <Input
            autoFocus
            type="password"
            value={token}
            onChange={(e) => {
              setToken(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
            }}
            placeholder="Paste your Immersal developer token"
          />
          {error ? (
            <FieldError>{error}</FieldError>
          ) : (
            <FieldHint>
              You can publish to web without it, but Immersal stories won't work on Android.
            </FieldHint>
          )}
        </Field>
      </DialogBody>

      <DialogFooter>
        <LureoButton
          variant="outline-2"
          suppressTooltip
          className="h-9 px-5 rounded-lureo-radius-button-pill"
          onClick={onClose}
          disabled={saving}
        >
          Continue without
        </LureoButton>
        <LureoButton
          variant="secondary"
          suppressTooltip
          className="h-9 px-4 rounded-lureo-radius-button-pill"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save token'}
        </LureoButton>
      </DialogFooter>
    </div>
  );
}
